/**
 * API Client for Buck Euchre
 * Handles communication between React frontend and Node.js backend
 */

import { getApiBaseUrl } from './utils/platform';

const API_BASE_URL = getApiBaseUrl();

/**
 * Save game data to database
 * @param {string} gameId - Unique game identifier
 * @param {number} playerCount - Number of players (3 or 4)
 * @param {string} wagerMode - 'none' or 'wager'
 * @param {Array} gameLog - Complete game log from logger.getLog()
 * @param {Object} aiLearningSnapshot - Current AI learning state
 */
export async function saveGameToDatabase(gameId, playerCount, wagerMode, gameLog, aiLearningSnapshot) {
  try {
    console.log(`💾 Saving game ${gameId} to database...`);

    const response = await fetch(`${API_BASE_URL}/games/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        gameId,
        playerCount,
        wagerMode,
        gameLog,
        aiLearningSnapshot
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save game');
    }

    const result = await response.json();
    console.log(`✅ Game saved successfully:`, result);
    return result;

  } catch (error) {
    console.error('❌ Error saving game to database:', error);

    // Don't throw - we don't want to break the game if database save fails
    // Just log the error and return failure status
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get AI learning statistics from database
 */
export async function getAIStats() {
  try {
    const response = await fetch(`${API_BASE_URL}/ai/stats`);

    if (!response.ok) {
      throw new Error('Failed to fetch AI stats');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching AI stats:', error);
    return null;
  }
}

/**
 * Get game history
 * @param {number} limit - Number of games to fetch (default: 10)
 */
export async function getGameHistory(limit = 10) {
  try {
    const response = await fetch(`${API_BASE_URL}/games?limit=${limit}`);

    if (!response.ok) {
      throw new Error('Failed to fetch game history');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching game history:', error);
    return { games: [] };
  }
}

/**
 * Get detailed game data
 * @param {string} gameId - Game identifier
 */
export async function getGameDetails(gameId) {
  try {
    const response = await fetch(`${API_BASE_URL}/games/${gameId}`);

    if (!response.ok) {
      throw new Error('Failed to fetch game details');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching game details:', error);
    return null;
  }
}

/**
 * Check if API server is available
 */
export async function checkAPIHealth() {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    return data.status === 'ok';

  } catch (error) {
    // API server not available
    return false;
  }
}

// ==================================================
// AUTHENTICATION API FUNCTIONS
// ==================================================

/**
 * Sign up new user
 */
export async function signupUser(email, password, firstName, lastName, nickname, zipCode, city, state, referralCode) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName, nickname, zipCode, city, state, referralCode: referralCode || undefined })
    });

    return await response.json();
  } catch (error) {
    console.error('Signup error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Change password
 */
export async function changePassword(token, currentPassword, newPassword) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });

    return await response.json();
  } catch (error) {
    console.error('Change password error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Verify email
 */
export async function verifyEmail(verificationToken) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify-email?token=${verificationToken}`);
    return await response.json();
  } catch (error) {
    console.error('Verify email error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Sign in existing user
 */
export async function signinUser(email, password) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    return await response.json();
  } catch (error) {
    console.error('Signin error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get current user from token
 */
export async function getCurrentUser(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Get user error:', error);
    return null;
  }
}

/**
 * Update user profile
 */
export async function updateProfile(token, profileData) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });

    return await response.json();
  } catch (error) {
    console.error('Profile update error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user balances
 */
export async function getUserBalances(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/balance`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Balance fetch error:', error);
    return null;
  }
}

/**
 * Update user balance after game (win/loss)
 */
export async function updateUserBalance(token, amountChange, wagerMode) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/balance/update`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ amountChange, wagerMode })
    });

    return await response.json();
  } catch (error) {
    console.error('Balance update error:', error);
    return { success: false, error: error.message };
  }
}

// ==================================================
// GAME SAVE/RESUME API FUNCTIONS
// ==================================================

/**
 * Save game snapshot for resume later
 */
export async function saveGameSnapshot(token, gameId, gameStateJson) {
  try {
    const response = await fetch(`${API_BASE_URL}/games/snapshot`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ gameId, gameStateJson })
    });
    return await response.json();
  } catch (error) {
    console.error('Save snapshot error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get user's active (in-progress) games
 */
export async function getActiveGames(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/games/active`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { games: [] };
    return await response.json();
  } catch (error) {
    console.error('Get active games error:', error);
    return { games: [] };
  }
}

/**
 * Resume a game from snapshot
 */
export async function resumeGame(token, gameId) {
  try {
    const response = await fetch(`${API_BASE_URL}/games/${gameId}/resume`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Resume game error:', error);
    return null;
  }
}

/**
 * Forfeit a game (loses buy-in wager)
 */
export async function forfeitGame(token, gameId) {
  try {
    const response = await fetch(`${API_BASE_URL}/games/${gameId}/forfeit`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Forfeit game error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get AI player balances
 */
export async function getAIBalances(names) {
  try {
    const response = await fetch(`${API_BASE_URL}/ai/balances?names=${encodeURIComponent(names.join(','))}`);
    if (!response.ok) return [];
    const data = await response.json();
    return data.balances || [];
  } catch (error) {
    console.error('Get AI balances error:', error);
    return [];
  }
}

/**
 * Update AI player balances after game
 */
export async function updateAIBalances(updates) {
  try {
    const response = await fetch(`${API_BASE_URL}/ai/balances/update`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    return await response.json();
  } catch (error) {
    console.error('Update AI balances error:', error);
    return { success: false, error: error.message };
  }
}

// ==================================================
// ANALYTICS API FUNCTIONS
// ==================================================

export async function getPlayerLeaderboard(limit = 20) {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/leaderboard?limit=${limit}`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('Leaderboard fetch error:', error);
    return [];
  }
}

export async function getPlayerStats(token, userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/player/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Player stats fetch error:', error);
    return null;
  }
}

export async function getAILeaderboard() {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/ai-leaderboard`);
    if (!response.ok) return [];
    return await response.json();
  } catch (error) {
    console.error('AI leaderboard fetch error:', error);
    return [];
  }
}

export async function getAIPlayerDetailStats(aiName) {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/ai/${encodeURIComponent(aiName)}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('AI player stats fetch error:', error);
    return null;
  }
}

export async function getTrumpCallingAnalytics() {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/trump-stats`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Trump analytics fetch error:', error);
    return null;
  }
}

export async function getSiteStats() {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/site-stats`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Site stats fetch error:', error);
    return null;
  }
}

export async function getAITrainingParams() {
  try {
    const response = await fetch(`${API_BASE_URL}/ai/training-params`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('AI training params fetch error:', error);
    return null;
  }
}

// ==================================================
// ADMIN API FUNCTIONS
// ==================================================

/**
 * Claim daily login reward
 */
export async function claimDailyReward(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/rewards/daily-login`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Claim daily reward error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get parameterized leaderboard
 */
export async function getLeaderboardByMetric(metric, timeframe = 'alltime', limit = 50, region = '') {
  try {
    let url = `${API_BASE_URL}/analytics/leaderboard/${metric}?timeframe=${timeframe}&limit=${limit}`;
    if (region) url += `&region=${encodeURIComponent(region)}`;
    const response = await fetch(url);
    return await response.json();
  } catch (error) {
    console.error('Leaderboard error:', error);
    return [];
  }
}

export async function getRegionStats() {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/region-stats`);
    return await response.json();
  } catch (error) {
    console.error('Region stats error:', error);
    return [];
  }
}

// ==================================================
// SEASONS API FUNCTIONS
// ==================================================

export async function getSeasons() {
  try {
    const response = await fetch(`${API_BASE_URL}/seasons`);
    return await response.json();
  } catch (error) {
    console.error('Seasons error:', error);
    return { seasons: [] };
  }
}

export async function getCurrentSeason() {
  try {
    const response = await fetch(`${API_BASE_URL}/seasons/current`);
    return await response.json();
  } catch (error) {
    console.error('Current season error:', error);
    return { season: null };
  }
}

export async function getSeasonDetail(seasonId) {
  try {
    const response = await fetch(`${API_BASE_URL}/seasons/${seasonId}`);
    return await response.json();
  } catch (error) {
    console.error('Season detail error:', error);
    return null;
  }
}

export async function getSeasonStandings(seasonId, metric = '') {
  try {
    let url = `${API_BASE_URL}/seasons/${seasonId}/standings`;
    if (metric) url += `?metric=${metric}`;
    const response = await fetch(url);
    return await response.json();
  } catch (error) {
    console.error('Season standings error:', error);
    return { standings: [] };
  }
}

export async function getUserSeasonHistory(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/season-history`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Season history error:', error);
    return { history: [] };
  }
}

export async function adminCreateSeason(token, name, theme, startDate, endDate) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/seasons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, theme, startDate, endDate })
    });
    return await response.json();
  } catch (error) {
    console.error('Create season error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminStartSeason(token, seasonId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/seasons/${seasonId}/start`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function adminEndSeason(token, seasonId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/seasons/${seasonId}/end`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ==================================================
// ADMIN API FUNCTIONS
// ==================================================

/**
 * Get all users (admin only)
 */
export async function adminGetUsers(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { users: [] };
    return await response.json();
  } catch (error) {
    console.error('Admin get users error:', error);
    return { users: [] };
  }
}

/**
 * Admin add balance to user
 */
export async function adminAddBalance(token, userId, playAmount, realAmount, reason) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/balance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ playAmount, realAmount, reason })
    });
    return await response.json();
  } catch (error) {
    console.error('Admin add balance error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin update user profile
 */
export async function adminUpdateProfile(token, userId, profileData) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    return await response.json();
  } catch (error) {
    console.error('Admin update profile error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin set balance (SET, not add)
 */
export async function adminSetBalance(token, userId, playMoneyBalance, realMoneyBalance, reason) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/set-balance`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ playMoneyBalance, realMoneyBalance, reason })
    });
    return await response.json();
  } catch (error) {
    console.error('Admin set balance error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin set membership tier
 */
export async function adminSetMembership(token, userId, tier, expiresAt) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/set-membership`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ userId, tier, expiresAt: expiresAt || null })
    });
    return await response.json();
  } catch (error) {
    console.error('Admin set membership error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin delete user account
 */
export async function adminDeleteUser(token, userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Admin delete user error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Zipcode lookup — returns city/state options
 */
export async function lookupZipcode(zip) {
  try {
    const response = await fetch(`${API_BASE_URL}/zipcode/${zip}`);
    if (!response.ok) return { places: [] };
    return await response.json();
  } catch (error) {
    return { places: [] };
  }
}

/**
 * Admin reset password (no current password needed)
 */
export async function adminResetPassword(token, userId, newPassword) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ newPassword })
    });
    return await response.json();
  } catch (error) {
    console.error('Admin reset password error:', error);
    return { success: false, error: error.message };
  }
}

// ==================================================
// AI SIMULATION API FUNCTIONS (Admin only)
// ==================================================

export async function adminStartSimulation(token, config) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(config)
    });
    return await response.json();
  } catch (error) {
    console.error('Start simulation error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminGetSimulationHistory(token, limit = 20) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/history?limit=${limit}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { runs: [] };
    return await response.json();
  } catch (error) {
    console.error('Get simulation history error:', error);
    return { runs: [] };
  }
}

export async function adminGetSimulationRun(token, runId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/run/${runId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Get simulation run error:', error);
    return null;
  }
}

export async function adminApplySimulationParams(token, runId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/apply/${runId}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Apply simulation params error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminRunAndApplySimulation(token, config) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/run-and-apply`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config),
    });
    return await response.json();
  } catch (error) {
    console.error('Run-and-apply simulation error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminSweepSimulation(token, config) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/simulation/sweep`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(config),
    });
    return await response.json();
  } catch (error) {
    console.error('Sweep simulation error:', error);
    return { success: false, error: error.message };
  }
}

// ==================================================
// TOURNAMENT API FUNCTIONS
// ==================================================

export async function getTournaments(status) {
  try {
    const url = status ? `${API_BASE_URL}/tournaments?status=${status}` : `${API_BASE_URL}/tournaments`;
    const response = await fetch(url);
    if (!response.ok) return { tournaments: [] };
    return await response.json();
  } catch (error) {
    console.error('Get tournaments error:', error);
    return { tournaments: [] };
  }
}

export async function getTournamentDetail(tournamentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/tournaments/${tournamentId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Get tournament detail error:', error);
    return null;
  }
}

export async function registerForTournament(token, tournamentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/tournaments/${tournamentId}/register`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Register for tournament error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminCreateTournament(token, data) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await response.json();
  } catch (error) {
    console.error('Create tournament error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminStartTournament(token, tournamentId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/${tournamentId}/start`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Start tournament error:', error);
    return { success: false, error: error.message };
  }
}

export async function adminReportMatchResult(token, matchId, winnerId) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/tournaments/match/${matchId}/result`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ winnerId })
    });
    return await response.json();
  } catch (error) {
    console.error('Report match result error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get active community rooms
 */
export async function getActiveRooms() {
  try {
    const response = await fetch(`${API_BASE_URL}/community/rooms`);
    if (!response.ok) return { rooms: [] };
    return await response.json();
  } catch (error) {
    console.error('Get active rooms error:', error);
    return { rooms: [] };
  }
}

/**
 * Get user's community game history
 */
export async function getCommunityHistory(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/community/history`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { history: [] };
    return await response.json();
  } catch (error) {
    console.error('Community history error:', error);
    return { history: [] };
  }
}

/**
 * Upload profile picture (base64)
 */
export async function uploadProfilePicture(token, imageData) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/profile-picture`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ imageData })
    });
    return await response.json();
  } catch (error) {
    console.error('Profile picture upload error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get public profile for a user
 */
export async function getPublicProfile(userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/profile/${userId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Public profile fetch error:', error);
    return null;
  }
}

/**
 * Get current user's badges
 */
export async function getUserBadges(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/badges`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { badges: [] };
    return await response.json();
  } catch (error) {
    console.error('Get badges error:', error);
    return { badges: [] };
  }
}

/**
 * Get all AI player stats (admin only)
 */
export async function adminGetAIStats(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/ai/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { aiPlayers: [] };
    return await response.json();
  } catch (error) {
    console.error('Admin get AI stats error:', error);
    return { aiPlayers: [] };
  }
}

/**
 * Get referral stats for current user
 */
export async function getReferralStats(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/referral-stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { referralCode: '', referralCount: 0, totalBucksEarned: 0, referredPlayers: [] };
    return await response.json();
  } catch (error) {
    console.error('Get referral stats error:', error);
    return { referralCode: '', referralCount: 0, totalBucksEarned: 0, referredPlayers: [] };
  }
}

/**
 * Submit a report against a user
 */
export async function submitReport(token, reportedUserId, reportType, reportedContent) {
  try {
    const response = await fetch(`${API_BASE_URL}/reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ reportedUserId, reportType, reportedContent })
    });
    return await response.json();
  } catch (error) {
    console.error('Submit report error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Admin: Get reports
 */
export async function adminGetReports(token, status) {
  try {
    const url = status ? `${API_BASE_URL}/admin/reports?status=${status}` : `${API_BASE_URL}/admin/reports`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { reports: [] };
    return await response.json();
  } catch (error) {
    console.error('Get reports error:', error);
    return { reports: [] };
  }
}

/**
 * Admin: Take action on a report
 */
export async function adminActionReport(token, reportId, action) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/reports/${reportId}/action`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ action })
    });
    return await response.json();
  } catch (error) {
    console.error('Action report error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * List/search clubs
 */
export async function getClubs(search) {
  try {
    const url = search ? `${API_BASE_URL}/clubs?search=${encodeURIComponent(search)}` : `${API_BASE_URL}/clubs`;
    const response = await fetch(url);
    if (!response.ok) return { clubs: [] };
    return await response.json();
  } catch (error) {
    console.error('Get clubs error:', error);
    return { clubs: [] };
  }
}

/**
 * Get club detail
 */
export async function getClubDetail(clubId) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.error('Get club detail error:', error);
    return null;
  }
}

/**
 * Create a new club
 */
export async function createClub(token, name, description, color, membershipType) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, description, color, membershipType })
    });
    return await response.json();
  } catch (error) {
    console.error('Create club error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Join a club
 */
export async function joinClub(token, clubId) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/join`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Join club error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Leave a club
 */
export async function leaveClub(token, clubId) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/leave`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    console.error('Leave club error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Kick a member from a club
 */
export async function kickClubMember(token, clubId, userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/kick`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ userId })
    });
    return await response.json();
  } catch (error) {
    console.error('Kick member error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Promote a member to captain
 */
export async function promoteClubMember(token, clubId, userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/promote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ userId })
    });
    return await response.json();
  } catch (error) {
    console.error('Promote member error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get current user's club
 */
export async function getUserClub(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/club`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { club: null };
    return await response.json();
  } catch (error) {
    console.error('Get user club error:', error);
    return { club: null };
  }
}

// ==================================================
// MEMBERSHIP & PAYMENTS API
// ==================================================

export async function getUserSubscription(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/subscription`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { tier: 'free' };
    return await response.json();
  } catch (error) {
    console.error('Get subscription error:', error);
    return { tier: 'free' };
  }
}

export async function createCheckout(token, type, plan, packId) {
  try {
    const response = await fetch(`${API_BASE_URL}/payments/create-checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ type, plan, packId })
    });
    return await response.json();
  } catch (error) {
    console.error('Create checkout error:', error);
    return { success: false, error: error.message };
  }
}

export async function getUserPurchases(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/purchases`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { purchases: [] };
    return await response.json();
  } catch (error) {
    console.error('Get purchases error:', error);
    return { purchases: [] };
  }
}

// ==================================================
// FIRST WEEK CHALLENGE API
// ==================================================

export async function getFirstWeekProgress(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/first-week-progress`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { expired: true, challenges: [] };
    return await response.json();
  } catch(error) {
    return { expired: true, challenges: [] };
  }
}

// ==================================================
// NOTIFICATION API
// ==================================================

export async function getNotifications(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { notifications: [], unreadCount: 0 };
    return await response.json();
  } catch (error) {
    console.error('Get notifications error:', error);
    return { notifications: [], unreadCount: 0 };
  }
}

export async function markNotificationRead(token, notificationId) {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/${notificationId}/read`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false };
  }
}

export async function markAllNotificationsRead(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false };
  }
}

export async function getNotificationPrefs(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/notification-prefs`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { prefs: {} };
    return await response.json();
  } catch (error) {
    return { prefs: {} };
  }
}

export async function updateNotificationPrefs(token, prefs) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/notification-prefs`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(prefs)
    });
    return await response.json();
  } catch (error) {
    return { success: false };
  }
}

// ==================================================
// ONBOARDING API
// ==================================================

export async function completeOnboarding(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/onboarding-complete`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false };
  }
}

// ==================================================
// AD REWARDS API
// ==================================================

export async function claimAdReward(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/rewards/ad-watched`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function getAdRewardConfig(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/rewards/ad-config`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { baseAmount: 15, bonusRange: 11, maxPerDay: 5, remaining: 0 };
  }
}

// ==================================================
// SPONSORS API
// ==================================================

export async function getActiveSponsors() {
  try {
    const response = await fetch(`${API_BASE_URL}/sponsors/active`);
    if (!response.ok) return { sponsors: [] };
    return await response.json();
  } catch (error) {
    return { sponsors: [] };
  }
}

// ==================================================
// ADMIN DASHBOARD API
// ==================================================

export async function getTurnedUpStats(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/turned-up-stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { suits: [], total: 0 };
    return await response.json();
  } catch (error) {
    console.error('Turned-up stats fetch error:', error);
    return { suits: [], total: 0 };
  }
}

export async function getAdminDashboard(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    return null;
  }
}

export async function getAdminSignupTrend(token, days = 30) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/dashboard/signups?days=${days}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { trend: [] };
    return await response.json();
  } catch (error) {
    return { trend: [] };
  }
}

export async function getAntiCheatFlags(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/anti-cheat/flags`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { multiAccounts: [] };
    return await response.json();
  } catch (error) {
    return { multiAccounts: [] };
  }
}

export async function adminCreateSponsor(token, name, logoUrl, websiteUrl, tier) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/sponsors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ name, logoUrl, websiteUrl, tier })
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ─── Admin Game Viewing ─────────────────────────────────────────────────────

export async function getAdminActiveRooms(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/active-rooms`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) return { rooms: [] };
    return await response.json();
  } catch (error) {
    return { rooms: [] };
  }
}

export async function createShowcaseGame(token, playerCount = 3) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/showcase-game`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ playerCount })
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ─── PROMO CODES ──────────────────────────────────────────────────────────

export async function redeemPromoCode(token, code) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/redeem-promo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ code })
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function adminGetPromoCodes(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function adminCreatePromoCode(token, data) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/promo-codes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

export async function adminUpdatePromoCode(token, promoId, data) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/promo-codes/${promoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(data)
    });
    return await response.json();
  } catch (error) {
    return { success: false, error: error.message };
  }
}

// ==================================================
// PHASE 2: LEADERBOARD NUDGE
// ==================================================

export async function getLeaderboardNudge(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/leaderboard/nudge`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { position: 0, totalPlayers: 0, nearbyRivals: [] };
  }
}

// ==================================================
// PHASE 3: FRIENDS, TIPS, RIVALRIES
// ==================================================

export async function getFriends(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { friends: [], pending: [] };
  }
}

export async function sendFriendRequest(token, friendId, nickname) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ friendId, nickname })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function acceptFriendRequest(token, friendshipId) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ friendshipId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function removeFriend(token, friendId) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends/${friendId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function blockUser(token, userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends/block`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ userId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function challengeFriend(token, friendId) {
  try {
    const response = await fetch(`${API_BASE_URL}/friends/challenge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ friendId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function sendTip(token, toUserId, amount, gameId) {
  try {
    const response = await fetch(`${API_BASE_URL}/tips`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ toUserId, amount, gameId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function getSportsmanship(userId) {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${userId}/sportsmanship`);
    return await response.json();
  } catch (error) {
    return { tipsGiven: 0, tipsReceived: 0, score: 0 };
  }
}

export async function getRivalries(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/rivalries`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return [];
  }
}

export async function getClubRankings() {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/rankings`);
    return await response.json();
  } catch (error) {
    return [];
  }
}

export async function updateClubDues(token, clubId, enabled, amount) {
  try {
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/dues`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ enabled, amount }),
    });
    return await response.json();
  } catch (error) {
    return { error: 'Failed to update dues' };
  }
}

// ==================================================
// PHASE 4: COSMETIC SHOP & ECONOMY
// ==================================================

export async function getShopItems(token, type) {
  try {
    let url = `${API_BASE_URL}/shop`;
    if (type) url += `?type=${encodeURIComponent(type)}`;
    const response = await fetch(url);
    return await response.json();
  } catch (error) {
    return [];
  }
}

export async function purchaseCosmetic(token, itemId) {
  try {
    const response = await fetch(`${API_BASE_URL}/shop/purchase`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ itemId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function getInventory(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/inventory`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return [];
  }
}

export async function equipCosmetic(token, itemId) {
  try {
    const response = await fetch(`${API_BASE_URL}/inventory/equip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ itemId })
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function claimAdRecovery(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/rewards/ad-recovery`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
}

export async function getEconomyHealth(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/economy-health`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { avgBankroll: 0, medianBankroll: 0, brokePct: 0, giniCoefficient: 0, interventions: [] };
  }
}

// ─── ADMIN ACCOUNTING ──────────────────────────────────────────────────────

export async function adminGetTransactions(token, params = {}) {
  try {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/admin/accounting/transactions?${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { transactions: [], total: 0 };
  }
}

export async function adminGetTransactionSummary(token, params = {}) {
  try {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/admin/accounting/summary?${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { summary: [] };
  }
}

export async function adminGetPromoUsage(token, params = {}) {
  try {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/admin/accounting/promo-usage?${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { uses: [], total: 0 };
  }
}

export async function adminGetAdRewards(token, params = {}) {
  try {
    const qs = new URLSearchParams(params).toString();
    const response = await fetch(`${API_BASE_URL}/admin/accounting/ad-rewards?${qs}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { rewards: [], total: 0 };
  }
}

export async function adminGetMemberships(token) {
  try {
    const response = await fetch(`${API_BASE_URL}/admin/accounting/memberships`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return await response.json();
  } catch (error) {
    return { members: [], purchases: [] };
  }
}
